import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { apiClient } from '@/lib/apiClient'
import { queryClient } from '@/lib/queryClient'
import { getInitData, isInsideTelegram } from '@/lib/telegram'
import {
  setSessionToken,
  clearSession,
  persistBrowserSessionToken,
  getPersistedBrowserSessionToken,
  clearPersistedBrowserSessionToken,
} from '@/lib/session'
import type { AuthResponse, MiniAppStatus, MiniAppUser } from '@/types/miniApp'
import type { TelegramLoginWidgetUser } from '@/components/TelegramLoginWidget'

interface AuthState {
  status: MiniAppStatus | 'loading' | 'error' | 'needs_login'
  user: MiniAppUser | null
  error: string | null
  retry: () => void
  /** P0.54 (29/08/2026): completa el login desde navegador externo con el resultado del
   * Telegram Login Widget -- nunca se llama dentro de Telegram WebApp (ahí `initData` siempre
   * gana). */
  loginWithTelegram: (widgetUser: TelegramLoginWidgetUser) => Promise<void>
  /** P0.54, Sección 15: cierra la sesión de navegador (revoca el token en el backend, limpia
   * el respaldo persistido). Nunca borra usuario/plan/banca/apuestas -- ver
   * `POST /api/mini-app/logout`. */
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

const DEV_TELEGRAM_USER_ID = (import.meta as unknown as { env: Record<string, string> }).env
  .VITE_DEV_TELEGRAM_USER_ID

/** P0.54 (29/08/2026, Sección 11/12 del encargo): orden de prioridad de autenticación --
 * `initData` de Telegram WebApp SIEMPRE gana cuando existe (máxima autoridad, se revalida
 * fresco en cada apertura); si no hay `initData`, el bypass de desarrollo (solo funciona si
 * el backend también lo tiene habilitado); si ninguno aplica, intenta reanudar una sesión de
 * navegador ya persistida (`GET /api/mini-app/me`); si tampoco hay nada, `null` -- el
 * `AuthProvider` lo traduce en mostrar la pantalla de login, nunca un error técnico. */
async function performAuth(): Promise<AuthResponse | null> {
  const initData = getInitData()
  if (initData) {
    const { data } = await apiClient.post<AuthResponse>('/api/mini-app/auth', { init_data: initData })
    return data
  }
  if (DEV_TELEGRAM_USER_ID) {
    const { data } = await apiClient.post<AuthResponse>('/api/mini-app/auth', {
      dev_telegram_user_id: Number(DEV_TELEGRAM_USER_ID),
    })
    return data
  }
  const persisted = getPersistedBrowserSessionToken()
  if (persisted) {
    try {
      const { data } = await apiClient.get<MiniAppUser>('/api/mini-app/me', {
        headers: { Authorization: `Bearer ${persisted}` },
      })
      return { status: 'active', session_token: persisted, user: data }
    } catch {
      // Token persistido inválido/vencido/revocado -- se descarta, cae a mostrar login. Nunca
      // se reintenta con un token que el backend ya rechazó.
      clearPersistedBrowserSessionToken()
    }
  }
  return null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading')
  const [user, setUser] = useState<MiniAppUser | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    setError(null)

    performAuth()
      .then((result) => {
        if (cancelled) return
        if (result === null) {
          setStatus('needs_login')
          return
        }
        setSessionToken(result.session_token)
        // Solo se persiste fuera de Telegram (Sección 9 del encargo P0.54) -- dentro de
        // Telegram WebApp el token en memoria basta, `initData` fresco es la fuente real.
        if (!isInsideTelegram() && result.session_token) {
          persistBrowserSessionToken(result.session_token)
        }
        setUser(result.user)
        setStatus(result.status)
      })
      .catch((err) => {
        if (cancelled) return
        clearSession()
        // P0.39 (27/08/2026) + P0.54 (29/08/2026): fuera de un cliente Telegram real, el
        // motivo exacto del fallo es irrelevante para el usuario -- SIEMPRE se muestra la
        // pantalla de login (nunca el detalle técnico del backend), nunca un dead-end.
        if (!isInsideTelegram()) {
          setStatus('needs_login')
          return
        }
        setStatus('error')
        setError(err?.response?.data?.detail || 'No se pudo verificar tu identidad.')
      })

    return () => {
      cancelled = true
    }
  }, [attempt])

  const loginWithTelegram = useCallback(async (widgetUser: TelegramLoginWidgetUser) => {
    setStatus('loading')
    setError(null)
    try {
      const { data } = await apiClient.post<AuthResponse>('/api/mini-app/auth/telegram-login', widgetUser)
      setSessionToken(data.session_token)
      if (data.session_token) persistBrowserSessionToken(data.session_token)
      setUser(data.user)
      setStatus(data.status)
    } catch (err) {
      // Vuelve a la pantalla de login (nunca un error técnico crudo) con un mensaje breve --
      // el widget se puede reintentar de inmediato, no hace falta un botón "Reintentar" aparte.
      setStatus('needs_login')
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'No se pudo verificar tu identidad con Telegram. Intenta de nuevo.')
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/mini-app/logout')
    } catch {
      // Best-effort: aunque falle la llamada, la sesión local se limpia igual -- nunca deja
      // al usuario atrapado sin poder salir.
    }
    clearSession()
    clearPersistedBrowserSessionToken()
    // Limpia todo lo cacheado por React Query (picks, banca, historial...) -- un dispositivo
    // compartido nunca debe mostrarle a la siguiente persona que inicie sesión datos ya
    // obtenidos para la cuenta anterior.
    queryClient.clear()
    setUser(null)
    setError(null)
    setStatus('needs_login')
  }, [])

  return (
    <AuthContext.Provider
      value={{ status, user, error, retry: () => setAttempt((a) => a + 1), loginWithTelegram, logout }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>')
  return ctx
}
